import { Flex, Text } from "@chakra-ui/layout";
import PageTitle from "../components/PageTitle";
import DataTable from "../components/table/ServerSideTable";
import { useCommandsPaginated } from "../hooks/commands.hooks";
import CartIcon from "../components/icons/CartIcon";
import PrimaryButton from "../components/PrimaryButton";
import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { type Command, CommandStatus } from "../types/command.types";
import CommandTypeBadge from "../components/CommandTypeBadge";
import { format } from "date-fns";
import InlineUserAvatar from "../components/InlineUserAvatar";
import { NavLink } from "react-router-dom";
import { CheckIcon, DownloadIcon, Spinner } from "@chakra-ui/icons";
import {
	useBulkChangeCommandStatus,
	useGetCommandOrderFilesId,
} from "../mutations/command.mutations";
import { fetchFileContent } from "../mutations/file.mutations";
import { PDFDocument } from "pdf-lib";
import { useSimpleToast } from "../hooks/toast.hooks";
import { AcceptModal } from "../components/modals/AcceptModal";
import { getFilesContentInBatches } from "./orders/getFilesContentInBatches";

const FILE_DOWNLOAD_BATCH_SIZE = 5;

function OrdersToPrintPageTableNoData() {
	return (
		<Flex justify="center">
			<Flex w="100%" justifyContent="center">
				<Flex
					w="100%"
					rounded="xl"
					bg="white"
					textAlign="center"
					lineHeight="44px"
					color="gray.500"
					direction={"column"}
					justifyContent={"center"}
					alignItems={"center"}
					py="100px"
				>
					<Flex
						w="48px"
						h="48px"
						bg="zinc.50"
						border="solid 1px"
						borderColor="brand.100"
						justifyContent="center"
						alignItems="center"
						borderRadius={100}
						mr="8px"
					>
						<CartIcon w="24px" h="24px" color="brand.700"></CartIcon>
					</Flex>
					<Text
						mt={4}
						color={"gray.900"}
						fontSize={"20px"}
						lineHeight={"30px"}
						fontWeight={600}
					>
						Aucune commande
					</Text>
				</Flex>
			</Flex>
		</Flex>
	);
}

function OrdersToPrintPage() {
	const [isFileDownloading, setIsFileDownloading] = useState<boolean>(false);
	const [rowSelection, setRowSelection] = useState<{
		[key: string]: boolean;
	}>({});
	const [isPendingBulkUpdateConfirmation, setIsPendingBulkUpdateConfirmation] =
		useState<boolean>(false);
	const [bulkUpdatePayload, setBulkUpdatePayload] = useState<
		number[] | undefined
	>();
	const { mutateAsync: mutateBulkChangeCommandStatus } =
		useBulkChangeCommandStatus();
	const toast = useSimpleToast();
	const { refetch: refetchFilesId } = useGetCommandOrderFilesId({
		ids: Object.keys(rowSelection).map((key) => Number.parseInt(key)),
	});

	const downloadMergedPdf = async () => {
		if (Object.values(rowSelection).length === 0 || isFileDownloading) return;
		setIsFileDownloading(true);
		let { data } = await refetchFilesId();
		const pdfDoc = await PDFDocument.create();
		if (!data) {
			setIsFileDownloading(false);
			return;
		}

		const ordersWithoutFile = data.filter((el) => !el.purchaseOrderFileId);
		if (ordersWithoutFile.length > 1) {
			toast.info(
				"Problème",
				`Les commandes ${ordersWithoutFile
					.map((el) => el.exposedId)
					.join(", ")} n'ont pas de fichier de commande et sont donc ignorées.`,
			);
		} else if (ordersWithoutFile.length === 1) {
			toast.info(
				"Problème",
				`La commande ${ordersWithoutFile[0].exposedId} n'a pas de fichier de commande et est donc ignorée.`,
			);
		}

		const printableOrders = data.filter(
			(
				el,
			): el is { id: number; purchaseOrderFileId: number; exposedId: string } =>
				Boolean(el.purchaseOrderFileId),
		);

		if (printableOrders.length === 0) {
			setIsFileDownloading(false);
			return;
		}

		try {
			const results = await getFilesContentInBatches({
				fileIds: printableOrders,
				batchSize: FILE_DOWNLOAD_BATCH_SIZE,
				getContent: (order) => fetchFileContent(order.purchaseOrderFileId),
			});

			const downloadedOrders: typeof printableOrders = [];
			const failedOrders: typeof printableOrders = [];

			for (const result of results) {
				if (result.status === "success") {
					downloadedOrders.push(result.fileId);
					// Load the PDF file
					const pdf = await PDFDocument.load(result.content);
					// Copy pages from the loaded PDF to the new PDF
					const pages = await pdfDoc.copyPages(pdf, pdf.getPageIndices());
					pages.forEach((page) => pdfDoc.addPage(page));
				} else {
					failedOrders.push(result.fileId);
				}
			}

			if (failedOrders.length > 0) {
				toast.info(
					"Problème",
					`Le téléchargement du bon de commande a échoué pour ${failedOrders
						.map((el) => el.exposedId)
						.join(", ")}. ${
						failedOrders.length > 1 ? "Elles ont" : "Elle a"
					} été ignorée${failedOrders.length > 1 ? "s" : ""}.`,
				);
			}

			if (downloadedOrders.length === 0) {
				setIsFileDownloading(false);
				return;
			}

			// Save the merged PDF
			const mergedPdfBytes = await pdfDoc.save();

			// Download the merged PDF
			// @ts-expect-error
			const blob = new Blob([mergedPdfBytes], {
				type: "application/pdf",
			});
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = "bonsDeCommande.pdf";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			setIsPendingBulkUpdateConfirmation(true);
			setBulkUpdatePayload(downloadedOrders.map((el) => el.id));
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: "Erreur lors de la génération du PDF",
				"Erreur",
			);
		}
		setIsFileDownloading(false);
	};

	const handleBulkUpdate = async () => {
		if (bulkUpdatePayload === undefined) {
			setIsPendingBulkUpdateConfirmation(false);
			return;
		}
		try {
			await mutateBulkChangeCommandStatus({
				ids: bulkUpdatePayload,
				status: "RECEIVED",
			});
		} catch (err) {
			toast.error("Erreur lors de la mise à jour des commandes", "Erreur");
		}
		setBulkUpdatePayload(undefined);
		setIsPendingBulkUpdateConfirmation(false);
	};

	const columns = useMemo<ColumnDef<Command>[]>(() => {
		return [
			{
				accessorFn: (row) => row.exposedId,
				id: "id",
				header: "ID",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => row.center.name,
				id: "name",
				header: "Nom du centre",
				cell: (info) => (
					<NavLink to={`/contacts/centers/${info.row.original.centerId}`}>
						<Text color="brand.700">{info.row.original.center.name}</Text>
					</NavLink>
				),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) =>
					row.patient?.firstName + " " + row.patient?.lastName,
				id: "patientName",
				header: "Patient",
				cell: (info) =>
					info.row.original.patientId ? (
						<NavLink to={`/patients/${info.row.original.patientId}`}>
							<Text color="brand.700">
								{(info.row.original.patient?.firstName || "---") +
									" " +
									(info.row.original.patient?.lastName || "---")}
							</Text>
						</NavLink>
					) : (
						<Text color="gray-600">
							{(info.row.original.patient?.firstName || "---") +
								" " +
								(info.row.original.patient?.lastName || "---")}
						</Text>
					),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <CommandTypeBadge type={row.type} />,
				id: "type",
				header: "Type",
				cell: (info) => info.getValue(),
				enableSorting: false,
			},
			{
				accessorFn: (row) => format(row.createdAt, "dd/MM/yyyy"),
				id: "date",
				header: "Date de création",
				cell: (info) => info.getValue(),
				enableSorting: false,
			},
			{
				accessorFn: (row) => <InlineUserAvatar id={row.creatorId} />,
				id: "creator",
				header: "Créé par",
				cell: (info) => info.getValue(),
				enableSorting: false,
			},
		];
	}, []);
	return (
		<>
			<Flex justify="space-between">
				<PageTitle>À imprimer</PageTitle>
				<Flex gap={4}>
					<PrimaryButton
						w="fit-content"
						isDisabled={
							Object.values(rowSelection).length === 0 || isFileDownloading
						}
						onClick={downloadMergedPdf}
					>
						{isFileDownloading ? (
							<Spinner size="sm" mr={4} />
						) : (
							<DownloadIcon mr={4} />
						)}
						<Text>Télécharger</Text>
					</PrimaryButton>
				</Flex>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={useCommandsPaginated({
					filters: {
						type: ["THREED", "OTOSCAN", "REWORK", "SUPPLY"],
						status: CommandStatus.WAITING_FOR_RECEPTION,
					},
				})}
				noDataComponent={OrdersToPrintPageTableNoData}
				hasRowsSelection={true}
				rowSelection={rowSelection}
				setRowSelection={setRowSelection}
				w={["20rem", "auto", "24rem", "12rem", "10rem", "20rem"]}
				flexGrow={[0, 1, 0, 0, 0, 0]}
				rowSelectionLimit={50}
				canSelectAllRows={true}
			></DataTable>
			<AcceptModal
				isOpen={isPendingBulkUpdateConfirmation}
				onClose={() => setIsPendingBulkUpdateConfirmation(false)}
				onValidation={handleBulkUpdate}
				description={
					"Souhaitez-vous passer les commandes sélectionnées à l'état réceptionnée ?"
				}
				validationButtonText="Confirmer"
				discardButtonText="Annuler"
				title="Mise à jour des commandes"
				icon={<CheckIcon w="24px" h="24px" color="brand.700" />}
			/>
		</>
	);
}

export default OrdersToPrintPage;
